import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Users, Coins, Plus, X } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

const VALID_BUYINS = [5, 10, 25, 50];

/**
 * Tournaments — multi-pilot single-elimination wager brackets.
 *
 * Lists all open tournaments (4-pilot 1v1 brackets), lets pilots create
 * a new one with a level + buy-in, and renders each tournament card with
 * join/cancel CTAs and live participant counts.
 */
export default function Tournaments() {
  const [list, setList] = useState([]);
  const [bots, setBots] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createLevel, setCreateLevel] = useState(1);
  const [createBuyin, setCreateBuyin] = useState(5);
  // All new brackets are 1v1 duels — size is hard-locked to 2 on the
  // backend. Keeping the state var for the existing pot/copy math
  // helpers but it's no longer user-editable.
  const createSize = 2;
  const [createInviteOnly, setCreateInviteOnly] = useState(false);
  const [createAllowAiFill, setCreateAllowAiFill] = useState(true);
  // Skill-based bracket gate — when true, the host's current league is
  // stamped on the bracket and only pilots in the same tier can join.
  const [createLeagueLock, setCreateLeagueLock] = useState(false);
  // Pilot's current league (id, label, color) — drives the lock-toggle
  // copy and the badge on the host's own listing rows.
  const [myLeague, setMyLeague] = useState(null);

  // Public tier table — cached once for badge label/color lookups
  // when rendering listing rows with a `league_lock`.
  const [tierMap, setTierMap] = useState({});

  useEffect(() => {
    api
      .get("/leagues")
      .then((r) => {
        const m = {};
        (r.data?.tiers || []).forEach((t) => { m[t.id] = t; });
        setTierMap(m);
      })
      .catch(() => {});
  }, []);

  const refresh = async () => {
    try {
      const r = await api.get("/tournaments/open");
      setList(r.data.tournaments || []);
    } catch {
      /* noop */
    }
  };

  const refreshBots = async () => {
    try {
      const r = await api.get("/bots/leaderboard");
      setBots((r.data.bots || []).slice(0, 10));
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    refresh();
    refreshBots();
    // Fetch the pilot's current league once so the create modal can
    // show the auto-locked tier copy and the listing rows can render a
    // tier-coloured badge on brackets locked to a tier.
    api
      .get("/leagues/me")
      .then((r) => setMyLeague(r.data?.tier || null))
      .catch(() => {});
    const t = setInterval(refresh, 10000);
    const b = setInterval(refreshBots, 30000);
    return () => {
      clearInterval(t);
      clearInterval(b);
    };
  }, []);

  const create = async () => {
    if (creating) return;
    const lvl = Math.max(1, Math.min(3000, parseInt(createLevel || "1", 10) || 1));
    setCreating(true);
    try {
      const r = await api.post("/tournaments/create", {
        level: lvl,
        buyin: createBuyin,
        size: createSize,
        invite_only: createInviteOnly,
        allow_ai_fill: createAllowAiFill,
        league_lock: createLeagueLock && myLeague ? myLeague.id : null,
      });
      toast.success(`1v1 Duel created · ${createBuyin} ☾ buy-in`, {
        description: createInviteOnly && r.data.join_code
          ? `Private duel · code: ${r.data.join_code}`
          : `Level ${lvl} · waiting for 1 challenger · +${
              r.data.human_victory_bonus_per_kill || 5
            } ☾ if you beat a human`,
      });
      setShowCreate(false);
      setCreateInviteOnly(false);
      setCreateLeagueLock(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create tournament");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16 fade-in"
        data-testid="tournaments-page"
      >
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.4em] text-[#ffd700]/70 uppercase mb-2">
              // BRACKET MODE
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white">
              Tournaments
            </h1>
            <p className="text-sm text-white/55 mt-2 max-w-xl">
              1v1 duels on a fixed level seed. Pay a buy-in, beat the
              opponent in a single match, take the pot.{" "}
              <span className="text-[#ff007f]">Beat a human pilot</span> for a{" "}
              +5 ☾ bonus on top.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-[#ffd700] bg-[#ffd700]/10 hover:bg-[#ffd700]/20 transition font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#ffd700]"
            data-testid="tournament-create-btn"
          >
            <Plus className="w-4 h-4" /> New Tournament
          </button>
        </div>

        {/* Open list */}
        <div className="space-y-2" data-testid="tournament-list">
          {list.length === 0 && (
            <div className="border border-white/10 bg-black/30 p-6 text-center">
              <Trophy className="w-6 h-6 mx-auto text-white/30 mb-2" />
              <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/45">
                No open tournaments — be the first to start one!
              </div>
            </div>
          )}
          {list.map((t) => (
            <TournamentRow key={t.id} t={t} onAction={refresh} tierMap={tierMap} />
          ))}
        </div>

        {/* Meet the AI — bot career leaderboard */}
        {bots.length > 0 && (
          <section className="mt-12" data-testid="meet-the-ai-section">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#b026ff]/80 uppercase">
                  // MEET THE AI
                </div>
                <h2 className="font-heading text-xl text-white">
                  Bot Career Leaderboard
                </h2>
              </div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                Top 10 · sorted by W
              </div>
            </div>
            <div
              className="border border-[#b026ff]/30 bg-black/30"
              data-testid="meet-the-ai-leaderboard"
            >
              {bots.map((b, i) => {
                const total = b.matches_total || 0;
                const rate = total > 0
                  ? Math.round((b.win_rate || 0) * 100)
                  : 0;
                const accent =
                  i === 0
                    ? "text-[#ffd700]"
                    : i === 1
                      ? "text-[#c0c0c0]"
                      : i === 2
                        ? "text-[#cd7f32]"
                        : "text-white/65";
                return (
                  <Link
                    key={b.user_id}
                    to={`/bots/${encodeURIComponent(b.user_id)}`}
                    className="grid grid-cols-[24px_1fr_auto_auto] gap-2 sm:gap-4 px-3 py-2 border-b border-white/5 last:border-b-0 items-center hover:bg-[#b026ff]/5 transition"
                    data-testid={`bot-row-${b.user_id}`}
                  >
                    <div
                      className={`font-mono-cosmic text-[10px] tabular-nums ${accent}`}
                    >
                      #{i + 1}
                    </div>
                    <div className="font-mono-cosmic text-[11px] tracking-[0.1em] text-white/85 truncate">
                      {b.name}
                      <span className="text-[#b026ff]/55">
                        #{b.tag}
                      </span>
                    </div>
                    <div className="font-mono-cosmic text-[10px] tabular-nums text-white/55 hidden sm:block">
                      {total} matches
                    </div>
                    <div className="font-mono-cosmic text-[11px] tabular-nums text-right">
                      <span className="text-[#28e070]">{b.wins}</span>
                      <span className="text-white/35">–</span>
                      <span className="text-[#ff5577]">{b.losses}</span>
                      <span className="text-white/35 text-[9px] ml-1.5">
                        {rate}%
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/35 mt-2">
              These bots auto-fill empty seats after 3 minutes.
              Their wins and losses persist across every bracket.
            </div>
          </section>
        )}
      </main>

      {/* Create modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="tournament-create-modal"
        >
          <div
            className="relative w-full max-w-md border border-[#ffd700]/60 bg-gradient-to-b from-[#0a0716] to-[#05050a] p-6"
            style={{ boxShadow: "0 0 60px rgba(255,215,0,0.28)" }}
          >
            <button
              onClick={() => setShowCreate(false)}
              className="absolute top-2 right-2 text-white/60 hover:text-white p-1"
              data-testid="tournament-create-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700]/80 uppercase mb-1">
              // NEW TOURNAMENT
            </div>
            <div className="font-heading text-2xl text-white mb-5">
              Create Bracket
            </div>

            <label className="block mb-4">
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1.5">
                Level (1–3000)
              </div>
              <input
                type="number"
                min="1"
                max="3000"
                value={createLevel}
                onChange={(e) => setCreateLevel(e.target.value)}
                className="w-full bg-black/40 border border-white/15 focus:border-[#ffd700] px-3 py-2 text-white font-mono-cosmic text-sm outline-none"
                data-testid="tournament-create-level"
              />
            </label>

            <div
              className="mb-4 px-3 py-2 border border-[#ff007f]/30 bg-[#ff007f]/8 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#ff007f]"
              data-testid="tournament-create-duel-locked"
            >
              1V1 DUEL · winner takes the pot in one match
            </div>

            <div className="mb-5">
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1.5">
                Buy-in (☾)
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {VALID_BUYINS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setCreateBuyin(v)}
                    className={`py-2 border font-mono-cosmic text-xs tabular-nums transition ${
                      createBuyin === v
                        ? "border-[#ffd700] bg-[#ffd700]/15 text-[#ffd700]"
                        : "border-white/15 text-white/55 hover:text-white"
                    }`}
                    data-testid={`tournament-create-buyin-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <div className="text-[9px] text-white/45 font-mono-cosmic mt-2 leading-relaxed">
                Pot: <span className="text-white">{createBuyin * createSize} ☾</span>{" "}
                · Winner takes <span className="text-[#ffd700]">{createBuyin * createSize - Math.floor(createBuyin * createSize * 0.1)} ☾</span>{" "}
                (10% house cut){" "}
                <span className="text-[#ff007f]">
                  +5 ☾ if opponent is a human
                </span>
              </div>
            </div>

            <label
              className="flex items-center gap-2 mb-2 cursor-pointer select-none"
              data-testid="tournament-create-invite-only-label"
            >
              <input
                type="checkbox"
                checked={createInviteOnly}
                onChange={(e) => setCreateInviteOnly(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#ffd700]"
                data-testid="tournament-create-invite-only"
              />
              <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/65">
                Private bracket · invite-only
              </span>
            </label>

            <label
              className="flex items-center gap-2 mb-4 cursor-pointer select-none"
              data-testid="tournament-create-allow-ai-fill-label"
              title="Idle brackets older than 3 minutes auto-fill with AI bots so you don't get stuck waiting."
            >
              <input
                type="checkbox"
                checked={createAllowAiFill}
                onChange={(e) => setCreateAllowAiFill(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#b026ff]"
                data-testid="tournament-create-allow-ai-fill"
              />
              <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/65">
                AI fill · auto-bots after 3 min
              </span>
            </label>

            {myLeague && (
              <label
                className="flex items-center gap-2 mb-4 cursor-pointer select-none"
                data-testid="tournament-create-league-lock-label"
                title="Restrict the bracket to pilots in your current skill tier. Keeps new pilots from facing veterans on day one."
              >
                <input
                  type="checkbox"
                  checked={createLeagueLock}
                  onChange={(e) => setCreateLeagueLock(e.target.checked)}
                  className="w-3.5 h-3.5"
                  style={{ accentColor: myLeague.color }}
                  data-testid="tournament-create-league-lock"
                />
                <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/65">
                  Skill lock ·{" "}
                  <span
                    className="font-bold"
                    style={{ color: myLeague.color }}
                  >
                    {myLeague.label}
                  </span>{" "}
                  only
                </span>
              </label>
            )}

            <button
              onClick={create}
              disabled={creating}
              className="w-full py-3 border border-[#ffd700] bg-gradient-to-r from-[#ffd700]/20 to-[#ff007f]/20 hover:from-[#ffd700]/30 hover:to-[#ff007f]/30 transition-colors font-heading uppercase tracking-[0.2em] text-sm text-[#ffd700] disabled:opacity-50"
              data-testid="tournament-create-submit"
            >
              {creating ? "Creating…" : `Pay ${createBuyin} ☾ & Create`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TournamentRow({ t, onAction, tierMap = {} }) {
  const [busy, setBusy] = useState(false);
  const filled = t.participants.length;
  const lockTier = t.league_lock ? tierMap[t.league_lock] : null;
  const join = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${t.id}/join`);
      toast.success("Joined the bracket", {
        description: `Pot: ${(filled + 1) * t.buyin} ☾ · ${4 - (filled + 1)} more to start`,
      });
      await onAction();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Join failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-white/10 hover:border-[#ffd700]/30 bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center transition"
      data-testid={`tournament-row-${t.id}`}
    >
      <Link to={`/tournaments/${t.id}`} className="contents" data-testid={`tournament-row-${t.id}-link`}>
        <div className="flex flex-wrap items-center gap-3 hover:opacity-90 transition">
          <div className="font-heading text-xl text-white tabular-nums">
            LVL {t.level}
          </div>
          <div className="flex items-center gap-1 text-[#ffd700] font-mono-cosmic text-xs tabular-nums">
            <Coins className="w-3 h-3" /> {t.buyin} ☾
          </div>
          <div className="flex items-center gap-1 text-white/55 font-mono-cosmic text-xs tabular-nums">
            <Users className="w-3 h-3" /> {filled}/{t.size}
          </div>
          <div className="flex items-center gap-1 text-[#ff007f] font-mono-cosmic text-xs tabular-nums">
            <Trophy className="w-3 h-3" /> Pot: {t.pot} ☾
          </div>
          {lockTier && (
            <div
              className="px-1.5 py-0.5 border font-mono-cosmic text-[9px] tracking-[0.2em] uppercase tabular-nums"
              style={{
                borderColor: `${lockTier.color}55`,
                color: lockTier.color,
                background: `${lockTier.color}14`,
              }}
              title={`Only ${lockTier.label} pilots can join this bracket`}
              data-testid={`tournament-${t.id}-league-badge`}
            >
              {lockTier.label} only
            </div>
          )}
        </div>
      </Link>
      <div className="flex gap-2 justify-end md:justify-start">
        <Link
          to={`/tournaments/${t.id}`}
          className="px-3 py-1.5 border border-[#b026ff] text-[#b026ff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#b026ff]/10"
          data-testid={`tournament-${t.id}-detail-btn`}
        >
          DETAILS
        </Link>
        <button
          onClick={join}
          disabled={busy}
          className="px-3 py-1.5 border border-[#ffd700] text-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#ffd700]/10 disabled:opacity-50"
          data-testid={`tournament-${t.id}-join-btn`}
        >
          {busy ? "JOINING…" : `JOIN · ${t.buyin} ☾`}
        </button>
      </div>
    </div>
  );
}
